import { GuideMenu } from '@/components/GuideMenu';
import { Card } from '@/components/ui/card';
import { Link, createFileRoute } from '@tanstack/react-router';

const OverviewSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">Overview</h3>
    <p className="text-[#b8b8b8]">
      Mythea is under continuous development, and has real world costs. As such,
      it requires real funds to keep it going. You can show support for Mythea,
      by purchasing Servants, which can then be exchanged for extra game
      services. They can also be sold for gems to trade with, in the Arcane
      Market and Auction.
    </p>
  </div>
);

const PurchasingSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">
      Purchasing Servants
    </h3>
    <p className="text-[#b8b8b8]">
      Purchasing servants is accomplished via Paypal, which has been the world
      leader in online payments for over 10 years. Your payment details are
      never stored with us, and clicking one of the purchase options will take
      you to a secure external paypal connection. After purchase your, Servants
      should appear within a few minutes.
    </p>
    <div className="bg-white/[0.03] p-4 rounded-lg border border-white/10 mt-4">
      <p className="text-[#b8b8b8]">
        Servants can be sold for 100 gems each, and can also be used to access
        other premium features.
      </p>
    </div>
  </div>
);

const KingdomTransfersSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">
      Aiding Servants
    </h3>
    <p className="text-[#b8b8b8]">
      Servants can also be aided to another player in your kingdom. That makes
      them useful when you want to move long-term gem value to a kingdom mate
      who will handle market buying or auction bidding.
    </p>
    <div className="bg-white/[0.03] p-4 rounded-lg border border-white/10">
      <p className="text-[#b8b8b8]">
        Servant aid is handled separately from ordinary goods, so it works well
        as a clean support transfer when you do not want to spend a normal
        resource package.
      </p>
    </div>
  </div>
);

const PremiumFeaturesSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">Premium Features</h3>
    <div className="grid grid-cols-1 gap-4">
      <div className="bg-white/[0.03] p-4 rounded-lg border border-white/10">
        <h4 className="text-lg font-semibold text-[#f0d060] mb-2">Spells</h4>
        <p className="text-[#b8b8b8]">
          Premium users have a &quot;cast until mana is below X&quot; box on the
          magic page, allowing them to quickly repeat casting a repetitive
          spell.
        </p>
      </div>

      <div className="bg-white/[0.03] p-4 rounded-lg border border-white/10">
        <h4 className="text-lg font-semibold text-[#f0d060] mb-2">Thievery</h4>
        <p className="text-[#b8b8b8]">
          There is a repeat operation until stealth below X ability.
        </p>
      </div>

      <div className="bg-white/[0.03] p-4 rounded-lg border border-white/10">
        <h4 className="text-lg font-semibold text-[#f0d060] mb-2">Combat</h4>
        <p className="text-[#b8b8b8]">
          The user interface has an auto-calculate button for your army, a
          battle force is automatically selected with sufficient offence to
          defeat the selected target - based on your most recent intelligence
        </p>
      </div>
    </div>
  </div>
);

const RelatedPagesSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">Related Pages</h3>
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      {[
        {
          to: '/trade',
          title: 'Trade & Aid',
          description: 'Kingdom transfer rules, logistics, and aid planning.',
        },
        {
          to: '/market',
          title: 'The Market',
          description: 'How gems, stock, and item purchases fit together.',
        },
        {
          to: '/auction',
          title: 'Auction',
          description: 'Bidding rules, sitting behavior, and winning outcomes.',
        },
      ].map((item) => (
        <Link key={item.to} to={item.to}>
          <Card className="h-full p-4 transition-colors hover:border-[#d4af37]/40">
            <h4 className="text-lg font-semibold text-[#f0d060]">
              {item.title}
            </h4>
            <p className="mt-2 text-sm text-[#b8b8b8]">{item.description}</p>
          </Card>
        </Link>
      ))}
    </div>
  </div>
);

export const Route = createFileRoute('/gems/')({
  component: GemsPage,
});

function GemsPage() {
  return (
    <div className="min-h-screen bg-black text-white p-4">
      <div className="max-w-6xl mx-auto space-y-8">
        <GuideMenu currentPath="/gems" />

        {/* Main content section */}
        <section className="glass rounded-2xl p-6">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <img
              src="/images/misc/gems@2x.png"
              alt="Gems"
              width={80}
              height={80}
              className="shrink-0"
            />
            <h2 className="text-2xl font-bold">Gems & Premium Features</h2>
          </div>

          {/* Content sections */}
          <div className="space-y-12 text-[#b8b8b8] leading-relaxed">
            <OverviewSection />
            <PurchasingSection />
            <KingdomTransfersSection />
            <PremiumFeaturesSection />
            <RelatedPagesSection />
          </div>
        </section>
      </div>
    </div>
  );
}
